/**
 * Utility for detecting and showing reward celebrations for offline point gains
 */

const STORAGE_KEY = 'tfic_last_known_org_points';
const TRANSACTION_QUEUE_KEY = 'tfic_pending_transaction_toasts';
const TOASTED_TRANSACTIONS_KEY = 'tfic_toasted_transaction_ids';

export interface PendingTransaction {
  id: number;
  points: number;
  reason: string;
  missionTitle?: string;
  createdAt: string;
}

export const getStoredOrgPoints = (): number | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? parseInt(stored, 10) : null;
};

export const storeOrgPoints = (points: number): void => {
  localStorage.setItem(STORAGE_KEY, points.toString());
};

export const clearStoredOrgPoints = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Store pending transactions to show as toasts later
 */
export const storePendingTransactions = (transactions: PendingTransaction[]): void => {
  localStorage.setItem(TRANSACTION_QUEUE_KEY, JSON.stringify(transactions));
};

/**
 * Get and clear pending transactions
 */
export const getPendingTransactions = (): PendingTransaction[] => {
  const stored = localStorage.getItem(TRANSACTION_QUEUE_KEY);
  if (!stored) return [];
  
  try {
    const transactions = JSON.parse(stored);
    // Clear after reading
    localStorage.removeItem(TRANSACTION_QUEUE_KEY);
    return Array.isArray(transactions) ? transactions : [];
  } catch (e) {
    console.error('Failed to parse pending transactions', e);
    localStorage.removeItem(TRANSACTION_QUEUE_KEY);
    return [];
  }
};

/**
 * Get the set of transaction IDs that have already been toasted
 */
export const getToastedTransactionIds = (): Set<number> => {
  const stored = localStorage.getItem(TOASTED_TRANSACTIONS_KEY);
  if (!stored) return new Set();
  
  try {
    const ids = JSON.parse(stored);
    return new Set(Array.isArray(ids) ? ids : []);
  } catch (e) {
    console.error('Failed to parse toasted transaction IDs', e);
    return new Set();
  }
};

/**
 * Mark transaction IDs as toasted
 */
export const markTransactionsAsToasted = (transactionIds: number[]): void => {
  if (!transactionIds || transactionIds.length === 0) return;
  
  const existing = getToastedTransactionIds();
  const before = existing.size;
  transactionIds.forEach(id => {
    if (id && typeof id === 'number') {
      existing.add(id);
    }
  });
  const after = existing.size;
  console.log(`Marked ${after - before} transactions as toasted. Total toasted: ${after}`);
  console.log('New toasted IDs:', transactionIds);
  localStorage.setItem(TOASTED_TRANSACTIONS_KEY, JSON.stringify(Array.from(existing)));
};

/**
 * Clear all toasted transaction tracking (for testing/reset purposes)
 */
export const clearToastedTransactions = (): void => {
  console.log('Clearing all toasted transaction tracking');
  localStorage.removeItem(TOASTED_TRANSACTIONS_KEY);
};

/**
 * Debug helper: Log current state of reward tracking
 */
export const debugRewardState = (): void => {
  console.log('=== REWARD TRACKING STATE ===');
  console.log('Stored points:', getStoredOrgPoints());
  console.log('Toasted transaction IDs:', Array.from(getToastedTransactionIds()));
  console.log('LocalStorage keys:', Object.keys(localStorage).filter(k => k.startsWith('tfic_')));
  console.log('=============================');
};

/**
 * Check if user gained points while offline and return the difference
 */
export const checkForOfflineRewards = (currentPoints: number): number => {
  const lastKnownPoints = getStoredOrgPoints();
  
  if (lastKnownPoints === null) {
    // First time or no previous data - store current and don't show celebration
    storeOrgPoints(currentPoints);
    return 0;
  }
  
  const pointsGained = currentPoints - lastKnownPoints;
  
  if (pointsGained > 0) {
    // Don't update stored points yet - let the caller do it after marking transactions as toasted
    return pointsGained;
  }
  
  // No change or points decreased (shouldn't happen normally)
  return 0;
};
